import { useEffect, useRef, useState } from 'react';
import {
  Content, ContentFeedSection, ContentInteractionAction, MetaItem,
} from '../../../common/models/content/Content';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useTrackElementVisibilityInViewPort } from '../../../common/hooks/useTrackElementVisibilityInViewPort';

interface useTrackCategoryContentVisibilityProps {
  categoryContentList: Content[]
}
export const useTrackCategoryContentVisibility = ({
  categoryContentList,
}: useTrackCategoryContentVisibilityProps) => {
  const { trackContentInteraction } = useCustomEventTracking();
  const [contentIdVisibilitySet, setContentIdVisibilitySet] = useState(new Set<number>());
  const contentIdVisibilityRef = useRef<Set<number>>(new Set<number>());
  const trackElementVisibility = useTrackElementVisibilityInViewPort();

  useEffect(() => {
    contentIdVisibilityRef.current = contentIdVisibilitySet;
  }, [contentIdVisibilitySet]);

  // track category content cards impressions
  useEffect(() => {
    const contentCreatorAndTitle = {} as {
      [key: number]: {
        contentTitle: string;
        contentCreatorName: string | null;
        type: string;
        contentMetaData: MetaItem[];
        contentFeedSection?: ContentFeedSection;
        contentFeedSectionPosition?: number
      }
    };

    categoryContentList?.forEach((content) => {
      contentCreatorAndTitle[content.id] = {
        contentTitle: content?.attributes?.title,
        contentCreatorName: content?.attributes?.content_owner?.data?.attributes?.display_name,
        contentMetaData: content?.attributes?.metadata || [],
        type: content?.attributes?.type,
        ...(content?.contentFeedSection && { contentFeedSection: content?.contentFeedSection }),
        ...(content?.contentFeedSectionPosition &&
          { contentFeedSectionPosition: content?.contentFeedSectionPosition }),
      };
    });

    const categoryElementsToTrack = Array.from(document.querySelectorAll('[id*="optimus-content"]'))
      .filter((element) => {
        const contentId = Number(element.id.split('optimus-content-')[1]);
        return categoryContentList.some((content) => content.id === contentId);
      });
    const observers = trackElementVisibility(categoryElementsToTrack, (element) => {
      if (element) {
        const contentId = Number(element.id.split('optimus-content-')[1]);
        if (contentId && !contentIdVisibilityRef.current.has(contentId)) {
          trackContentInteraction(
            ContentInteractionAction.View,
            contentCreatorAndTitle[contentId]?.type?.replace(/^\w/, (c) => c.toUpperCase()),
            {
              contentId,
              contentTitle: contentCreatorAndTitle[contentId]?.contentTitle || '',
              contentCreatorName: contentCreatorAndTitle[contentId]?.contentCreatorName || '',
              type: contentCreatorAndTitle[contentId]?.type || '',
              contentMetaData: contentCreatorAndTitle[contentId]?.contentMetaData || [],
              ...(contentCreatorAndTitle[contentId]?.contentFeedSection &&
                { contentFeedSection: contentCreatorAndTitle[contentId].contentFeedSection }),
              ...(contentCreatorAndTitle[contentId]?.contentFeedSectionPosition &&
              {
                contentFeedSectionPosition:
                  contentCreatorAndTitle[contentId].contentFeedSectionPosition,
              }),
            },
          );
          setContentIdVisibilitySet((prevSet) => new Set([...prevSet, contentId]));
        }
      }
    });

    // Clean up the observers when the component unmounts
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryContentList, trackElementVisibility]);
};
